import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { updateCampaign } from 'api/campaigns';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { CAMPAIGN_TEAM_MULTITEAM_FUNDRAISING_SETTINGS_LINK } from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface MultiTeamFundraisingForm {
    isMultiTeamFundraisingEnabled: boolean;
    allowTeamCreationByMember: boolean;
    teamMemberLimit: string;
    hasTeamMemberLimit: boolean;
    hideTeamAmount: boolean;
}
const CampaignTeamMultiTeamFundraisingSettings = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: campaign } = useCurrentCampaign();
    const methods = Form.useGivebutterForm<MultiTeamFundraisingForm>({
        defaultValues: {
            isMultiTeamFundraisingEnabled: campaign?.enableMultiTeam?.active ?? false,
            allowTeamCreationByMember: !campaign?.disableCreatingNewTeams?.active,
            teamMemberLimit: campaign?.multiTeamLimit?.value ?? '',
            hasTeamMemberLimit: Boolean(campaign?.multiTeamLimit?.value),
            hideTeamAmount: campaign?.hideTeamRaised?.active ?? false,
        },
    });
    const { watch, control, path, handleSubmit } = methods;
    const [isMultiTeamEnabled, hasTeamMemberLimit] = watch([
        'isMultiTeamFundraisingEnabled',
        'hasTeamMemberLimit',
    ]);

    const { mutate: submit } = useMutation({
        mutationFn: (values: MultiTeamFundraisingForm) => {
            const teamMemberLimit = values.hasTeamMemberLimit
                ? unmaskNumber(values.teamMemberLimit)
                : null;

            const data = {
                settings: [
                    {
                        code: Settings.EnableMultiTeam.Code,
                        active: values.isMultiTeamFundraisingEnabled,
                    },
                    {
                        code: Settings.DisableCreatingNewTeams.Code,
                        active: !values.allowTeamCreationByMember,
                    },
                    {
                        code: Settings.MultiTeamLimit.Code,
                        value: teamMemberLimit,
                        active: teamMemberLimit !== null && teamMemberLimit > 0,
                    },
                    {
                        code: Settings.HideTeamRaised.Code,
                        active: values.hideTeamAmount,
                    },
                ],
            };

            return updateCampaign({ campaignId: campaign?.id!, data });
        },
        onSuccess: () => {
            toast.success(`Updated team fundraising settings successfully!`);

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaign.id),
                refetchType: 'none',
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? `Failed to update team fundraising settings`),
    });

    return (
        <Form.Provider {...methods}>
            <SettingsCard.Root>
                <SettingsCard.LeftSide>
                    <SettingsCard.Heading>Team fundraising</SettingsCard.Heading>
                    <p className={'text-sm text-gray-700'}>
                        Configure how you'd like teams to fundraise.
                    </p>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={CAMPAIGN_TEAM_MULTITEAM_FUNDRAISING_SETTINGS_LINK}
                        className={'text-sm'}
                    >
                        Read the guide
                    </a>
                    .
                </SettingsCard.LeftSide>
                <SettingsCard.RightSide>
                    <form
                        onSubmit={handleSubmit(submit as SubmitHandler<MultiTeamFundraisingForm>)}
                    >
                        <div className={'flex flex-col gap-8'}>
                            <div className={'flex flex-col gap-4'}>
                                <div className={'flex flex-col gap-2'}>
                                    <div className={'flex items-center justify-between gap-2'}>
                                        <Form.Label
                                            className={'text-base'}
                                            name={path('isMultiTeamFundraisingEnabled')}
                                        >
                                            Enable team fundraising
                                        </Form.Label>
                                        <Form.Switch
                                            control={control}
                                            name={'isMultiTeamFundraisingEnabled'}
                                        />
                                    </div>
                                    <p className={'text-sm text-gray-700'}>
                                        Create teams for your campaign, let individuals join those
                                        teams, and show a team leaderboard.
                                    </p>
                                </div>
                                {isMultiTeamEnabled && (
                                    <>
                                        <div className={'flex gap-2'}>
                                            <Form.UseFormCheckboxInput
                                                control={control}
                                                name={'allowTeamCreationByMember'}
                                            />
                                            <Form.Container>
                                                <Form.Label
                                                    className={'mb-0.5 leading-none'}
                                                    name={path('allowTeamCreationByMember')}
                                                >
                                                    Allow fundraisers to create new teams
                                                </Form.Label>
                                                <p className={'text-sm text-gray-700'}>
                                                    Uncheck this option if you don't want
                                                    individuals to be able to create new teams when
                                                    joining your campaign.
                                                </p>
                                            </Form.Container>
                                        </div>
                                        <div className={'flex flex-col gap-3'}>
                                            <div className={'flex items-start gap-2'}>
                                                <div className="flex h-auto">
                                                    <Form.UseFormCheckboxInput
                                                        control={control}
                                                        name={path('hasTeamMemberLimit')}
                                                    />
                                                </div>
                                                <Form.Container>
                                                    <Form.Label
                                                        className={'mb-0.5 leading-none'}
                                                        name={path('hasTeamMemberLimit')}
                                                    >
                                                        Limit per team
                                                    </Form.Label>
                                                    <p className={'text-sm text-gray-700'}>
                                                        Set a limit to the number of individuals
                                                        that can join a team.
                                                    </p>
                                                </Form.Container>
                                            </div>
                                            {hasTeamMemberLimit && (
                                                <div className={'w-64 pl-6'}>
                                                    <Form.TextInputRoot
                                                        control={control}
                                                        name={'teamMemberLimit'}
                                                        inputRootProps={{
                                                            mask: 'whole-number',
                                                        }}
                                                    >
                                                        <Form.TextInputForm
                                                            placeholder={'Unlimited'}
                                                        />
                                                    </Form.TextInputRoot>
                                                    <Form.UseFormError
                                                        name={path('teamMemberLimit')}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className={'flex gap-2'}>
                                            <Form.UseFormCheckboxInput
                                                control={control}
                                                name={'hideTeamAmount'}
                                            />
                                            <Form.Container>
                                                <Form.Label
                                                    className={'mb-0.5 leading-none'}
                                                    name={path('hideTeamAmount')}
                                                >
                                                    Hide amount raised per team
                                                </Form.Label>
                                                <p className={'text-sm text-gray-700'}>
                                                    Hide the total amount raised by each team.
                                                </p>
                                            </Form.Container>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div>
                                <Form.SubmitButton>Save</Form.SubmitButton>
                            </div>
                        </div>
                    </form>
                </SettingsCard.RightSide>
            </SettingsCard.Root>
        </Form.Provider>
    );
};

export { CampaignTeamMultiTeamFundraisingSettings };
